/**
 * OperationReview — the propose → review → apply review surface (TV1-1828, T22).
 *
 * Renders the current `OperationRun`'s computed change set in the shared
 * `DiffReviewGrid` and wires every interaction to the operations CDSE store:
 *   - row-approval toggle        → `operations/review/toggleRowApproval`
 *   - approve-all / clear        → `operations/review/approveAll` / `clearApproval`
 *   - Approve / Reject actions    → `operations/review/submitApproval` / `reject`
 *
 * The grid's `approvedIds` prop is sourced from the store's `approvedItemIds`
 * selector, and progress (`appliedCount` / `failedCount` / total) is read from
 * the apply-progress selectors.
 *
 * For an `external-irreversible` run, an EXPLICIT approval gate is shown before
 * the apply can be submitted: the operator must tick a confirmation checkbox
 * (gated on `run.riskClass === 'external-irreversible'`) — mirroring the
 * structural egress gate on the server side.
 *
 * This is a thin UI layer: no business logic, no API calls (those live in the
 * domain policies, T21). User interactions dispatch with `{ source: 'user' }`.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Progress,
  Stack,
  Text
} from '@mantine/core';
import { IconAlertTriangle, IconClipboardCheck } from '@tabler/icons-react';
import type { ChangeItemType } from '@bett3r-dev/teselly-domain';
import DiffReviewGrid, {
  DiffChangeItem,
  DiffColumn
} from '@components/application/SpreadsheetEditor/DiffReviewGrid';
import { operationsTestIds } from '@domains/operations/testids';
import {
  useOperationsSelector,
  useOperationsDispatch,
  selectors
} from '../state/store';
import classes from './OperationReview.module.css';

/**
 * Adapt a domain `ChangeItemType` to the grid's `DiffChangeItem`. The only
 * divergence between the two shapes is the status union: the domain uses
 * `'proposed'` for a freshly-computed, pending row while the grid uses
 * `'pending'`. They are semantically identical (both render the yellow
 * proposed-change style and are selectable), so we map them here.
 */
const toDiffItem = ( item: ChangeItemType ): DiffChangeItem => {
  // The domain `ChangeCell` types `old`/`new` as optional (freeform JSON), while
  // the grid's `DiffCell` requires both present; normalize each cell so an absent
  // value renders as a blank `null` rather than a type mismatch.
  const cells = Object.entries( item.cells ).reduce<DiffChangeItem['cells']>(
    ( acc, [ key, cell ]) => {
      acc[ key ] = { old: cell?.old ?? null, new: cell?.new ?? null };
      return acc;
    },
    {}
  );
  return {
    targetId: item.targetId,
    targetType: item.targetType,
    cells,
    status: item.status === 'proposed' ? 'pending' : item.status,
    note: item.note
  };
};

/**
 * Build the diff grid column layout from the change set. Every distinct cell key
 * across all items becomes an `affected` (old/new pair) column; the leading
 * identity column shows the target id as read-only context.
 */
const buildColumns = (
  items: ChangeItemType[],
  identityLabel: string
): DiffColumn[] => {
  const cellKeys = new Set<string>();
  items.forEach(( item ) => {
    Object.keys( item.cells ).forEach(( key ) => cellKeys.add( key ));
  });
  return [
    { key: 'targetId', label: identityLabel, affected: false },
    ...Array.from( cellKeys ).map(( key ) => ({ key, label: key, affected: true }))
  ];
};

/**
 * Per-operationType review-renderer registry (Kit Inference slice 4, ADR-070).
 *
 * Most operations (reprice, bulk-publish) review as a flat old→new grid, so the
 * DEFAULT renderer wraps the shared `DiffReviewGrid` — existing operations render
 * UNCHANGED. An operation whose change items carry HIERARCHY (a kit's BOM lives in
 * `cells.components.new`, already `BaseSchema<any>` — no kernel schema change) can
 * register a bespoke renderer keyed by its `operationType`; the review surface
 * dispatches to it without the kernel knowing the operation's internals.
 *
 * The registry is ADDITIVE: `registerOperationReviewRenderer` is called once at a
 * feature's registration site (e.g. the kit-inference domain module); anything
 * unregistered falls back to `DefaultDiffReviewRenderer`.
 */
export type OperationReviewRendererProps = {
  /** The domain change items for the current run. */
  changeItems: ChangeItemType[];
  /** The currently approved target subset. */
  approvedItemIds: string[];
  /** Toggle a single row's approval. */
  onToggleRow: ( targetId: string, checked: boolean ) => void;
  /** Toggle approve-all / clear. */
  onToggleAll: ( checked: boolean ) => void;
};

export type OperationReviewRenderer = ( props: OperationReviewRendererProps ) => ReactElement;

/** The default renderer — the flat `DiffReviewGrid` every operation used before. */
const DefaultDiffReviewRenderer: OperationReviewRenderer = ({
  changeItems,
  approvedItemIds,
  onToggleRow,
  onToggleAll
}) => {
  const { t } = useTranslation( 'operations' );
  const diffItems = useMemo(() => changeItems.map( toDiffItem ), [changeItems]);
  const columns = useMemo(
    () => buildColumns( changeItems, t( 'review_column_target', 'Objetivo' )),
    [ changeItems, t ]
  );
  return (
    <DiffReviewGrid
      items={diffItems}
      columns={columns}
      approvedIds={approvedItemIds}
      onToggleRow={onToggleRow}
      onToggleAll={onToggleAll}
    />
  );
};

const reviewRendererRegistry: Record<string, OperationReviewRenderer> = {};

/** Register a bespoke review renderer for an `operationType`. Additive. */
export const registerOperationReviewRenderer = (
  operationType: string,
  renderer: OperationReviewRenderer
): void => {
  reviewRendererRegistry[ operationType ] = renderer;
};

/** Resolve the renderer for an `operationType`, defaulting to `DiffReviewGrid`. */
export const getOperationReviewRenderer = (
  operationType?: string
): OperationReviewRenderer =>
  ( operationType ? reviewRendererRegistry[ operationType ] : undefined ) ?? DefaultDiffReviewRenderer;

const OperationReview = () => {
  const { t } = useTranslation( 'operations' );
  const dispatch = useOperationsDispatch();

  const run = useOperationsSelector(( s ) => s.review.run );
  const changeItems = useOperationsSelector(( s ) => selectors.changeItems( s ));
  const approvedItemIds = useOperationsSelector(( s ) => selectors.approvedItemIds( s ));
  const hasApproval = useOperationsSelector(( s ) => selectors.hasApproval( s ));
  const isReadyForReview = useOperationsSelector(( s ) => selectors.isReadyForReview( s ));
  const isSubmitting = useOperationsSelector(( s ) => s.loading.submitting );
  const isLoadingChangeSet = useOperationsSelector(( s ) => s.loading.changeSet );

  // Apply-progress selectors.
  const appliedCount = useOperationsSelector(( s ) => selectors.appliedCount( s ));
  const failedCount = useOperationsSelector(( s ) => selectors.failedCount( s ));
  const totalItems = useOperationsSelector(( s ) => selectors.totalItems( s ));
  const applyProgress = useOperationsSelector(( s ) => selectors.applyProgressPercentage( s ));
  const isTerminal = useOperationsSelector(( s ) => selectors.isTerminal( s ));

  // Explicit confirmation for external-irreversible runs (gated on riskClass).
  const isExternalIrreversible = run?.riskClass === 'external-irreversible';
  const [ confirmedIrreversible, setConfirmedIrreversible ] = useState( false );

  // Per-operationType renderer (defaults to DiffReviewGrid — existing operations
  // render unchanged). A hierarchical operation (e.g. kit inference) registers its
  // own renderer keyed by operationType.
  const ReviewRenderer = getOperationReviewRenderer( run?.operationType );

  const handleToggleRow = ( targetId: string, checked: boolean ) => {
    dispatch(
      {
        type: 'operations/review/toggleRowApproval',
        payload: { targetId, approved: checked }
      },
      { source: 'user' }
    );
  };

  const handleToggleAll = ( checked: boolean ) => {
    dispatch(
      checked
        ? { type: 'operations/review/approveAll', payload: {}}
        : { type: 'operations/review/clearApproval', payload: {}},
      { source: 'user' }
    );
  };

  const handleApprove = () => {
    dispatch(
      { type: 'operations/review/submitApproval', payload: {}},
      { source: 'user' }
    );
  };

  const handleReject = () => {
    dispatch(
      { type: 'operations/review/reject', payload: {}},
      { source: 'user' }
    );
  };

  // The approval action is enabled only when the run is ready, at least one row
  // is approved, and — for external-irreversible runs — the explicit
  // confirmation has been ticked.
  const canApprove =
    isReadyForReview &&
    hasApproval &&
    ( !isExternalIrreversible || confirmedIrreversible );

  const showProgress = run ? !isReadyForReview && totalItems > 0 : false;

  return (
    <div className={classes.container} data-testid={operationsTestIds.review}>
      <LoadingOverlay visible={isLoadingChangeSet} zIndex={200} overlayProps={{ blur: 2 }} />

      <Group justify="space-between" align="flex-start" className={classes.viewHeader}>
        <div>
          <Text className={classes.viewTitle}>
            {t( 'review_title', 'Revisar operación' )}
          </Text>
          <Text className={classes.viewSubtitle}>
            {t(
              'review_subtitle',
              'Revisá los cambios propuestos y aprobá o rechazá la operación.'
            )}
          </Text>
        </div>
        {run && (
          <Group gap="xs">
            <Badge color="gray" variant="light" data-testid={operationsTestIds.reviewType}>
              {t( `operation_type_${run.operationType}`, run.operationType )}
            </Badge>
            <Badge
              color={isExternalIrreversible ? 'red' : 'blue'}
              variant="light"
              data-testid={operationsTestIds.reviewRisk}
            >
              {t( `risk_class_${run.riskClass}`, run.riskClass )}
            </Badge>
            <Badge color="gray" variant="outline" data-testid={operationsTestIds.runStatusBadge}>
              {t( `run_status_${run.status}`, run.status )}
            </Badge>
            {/*
              Provenance (TV1-2135, ADR-133). A model-proposed run used to be
              byte-for-byte indistinguishable from a human button click here.
              Absent / 'user' renders nothing, so the existing flow is unchanged.
              NOTE: caller-asserted, not authenticated — an audit aid, never a gate.
            */}
            {run.proposedVia === 'assistant' && (
              <Badge
                color="violet"
                variant="light"
                data-testid={operationsTestIds.reviewProposedByAssistant}
              >
                {t( 'proposed_by_assistant', 'Propuesto por el asistente' )}
              </Badge>
            )}
          </Group>
        )}
      </Group>

      {!run ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateIcon}>
            <IconClipboardCheck size={20} />
          </div>
          <Text className={classes.emptyStateTitle}>
            {t( 'review_empty_title', 'No hay ninguna operación en revisión' )}
          </Text>
          <Text className={classes.emptyStateDescription}>
            {t(
              'review_empty_description',
              'Iniciá una operación desde una lista para revisar sus cambios acá.'
            )}
          </Text>
        </div>
      ) : (
        <Stack gap="md">
          <Paper withBorder className={classes.tableWrapper}>
            <ReviewRenderer
              changeItems={changeItems}
              approvedItemIds={approvedItemIds}
              onToggleRow={handleToggleRow}
              onToggleAll={handleToggleAll}
            />
          </Paper>

          {showProgress && (
            <Stack gap="xxs" data-testid={operationsTestIds.runProgress}>
              <Group justify="space-between">
                <Text fz="sm" c="dimmed">
                  {t( 'review_progress_label', 'Progreso de aplicación' )}
                </Text>
                <Text fz="sm" c="dimmed">
                  {t( 'review_progress_counts', '{{done}} de {{total}}', {
                    done: appliedCount + failedCount,
                    total: totalItems
                  })}
                  {failedCount > 0 &&
                    ` · ${t( 'review_progress_failed', '{{failed}} con error', {
                      failed: failedCount
                    })}`}
                </Text>
              </Group>
              <Progress
                value={applyProgress}
                color={failedCount > 0 ? 'orange' : 'primary'}
                striped
                animated={!isTerminal}
              />
            </Stack>
          )}

          {isExternalIrreversible && isReadyForReview && (
            <div className={classes.riskGate} data-testid={operationsTestIds.reviewRiskGate}>
              <Group gap="xs" mb="xs">
                <IconAlertTriangle size={18} color="var(--mantine-color-orange-6)" />
                <Text fw={600} fz="sm">
                  {t( 'review_irreversible_title', 'Acción irreversible y externa' )}
                </Text>
              </Group>
              <Text fz="sm" c="dimmed" mb="sm">
                {t(
                  'review_irreversible_description',
                  'Esta operación envía cambios a un canal externo y no se puede deshacer. Confirmá explícitamente antes de aplicar.'
                )}
              </Text>
              <Button
                variant={confirmedIrreversible ? 'filled' : 'outline'}
                color="orange"
                size="sm"
                radius="xs"
                styles={{ label: { fontSize: '12px' }}}
                leftSection={<IconAlertTriangle size={16} />}
                onClick={() => setConfirmedIrreversible(( prev ) => !prev )}
                data-testid={operationsTestIds.reviewConfirmIrreversible}
              >
                {confirmedIrreversible
                  ? t( 'review_irreversible_confirmed', 'Confirmado' )
                  : t(
                      'review_irreversible_confirm',
                      'Entiendo que esto es irreversible'
                    )}
              </Button>
            </div>
          )}

          {isReadyForReview && (
            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                color="red"
                size="sm"
                radius="xs"
                styles={{ label: { fontSize: '12px' }}}
                onClick={handleReject}
                loading={isSubmitting}
                data-testid={operationsTestIds.reject}
              >
                {t( 'review_reject', 'Rechazar' )}
              </Button>
              <Button
                size="sm"
                radius="xs"
                styles={{ label: { fontSize: '12px' }}}
                disabled={!canApprove}
                onClick={handleApprove}
                loading={isSubmitting}
                data-testid={operationsTestIds.apply}
              >
                {t( 'review_approve', 'Aprobar y aplicar' )}
              </Button>
            </Group>
          )}

          {!isReadyForReview && !showProgress && (
            <Alert color="gray" variant="light">
              {t(
                'review_not_ready',
                'La operación todavía no está lista para revisar.'
              )}
            </Alert>
          )}
        </Stack>
      )}
    </div>
  );
};

export default OperationReview;
